import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { csvParse } from 'd3-dsv';
import { Box, Chip, LinearProgress, Typography, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useParams } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, CellContextMenuEvent } from 'ag-grid-community';
import { useSetAtom } from 'jotai';
import useExtractionBatchTransformed from '../../hooks/useExtractionBatchTransformed';
import useGetAllExtractions from '../../hooks/useGetAllExtractions';
import { useDataValidation } from '../../hooks/useDataValidation';
import { useQueryClient } from '@tanstack/react-query';
import RefreshButton from '../../common/Buttons/RefreshButton';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import {
  buildValidationSummaryByExtractionId,
  getCellStyles,
} from '../../../utils/validationUtils';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';
import { validationDismissalDialogAtom } from '../../../atoms/spreadsheet-atoms';
import { ValidationDismissalReason } from '../../../types/validation-types';
import ValidationDismissalDialog from '../../review/ValidationDismissalDialog';
import TransformedGridContextMenu from './TransformedGridContextMenu';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import TransformedValidationErrorsTable from './TransformedValidationErrorsTable';

interface ValidationErrorDetail {
  id: number;
  field: string;
  errorDetail?: string;
  error?: string;
  dataset?: string;
  type?: string;
  extractionId?: string;
  dismissalReason?: ValidationDismissalReason | null;
}

type ErrorsByDocumentId = Record<string, ValidationErrorDetail[]>;

interface ExtractionBatchTransformedResultsProps {
  extractionBatchId: string;
}

const ExtractionBatchTransformedResults = ({
  extractionBatchId,
}: ExtractionBatchTransformedResultsProps) => {
  if (!extractionBatchId) {
    return null;
  }

  const { dpid } = useParams<{ dpid: string }>();
  const queryClient = useQueryClient();
  const [forceValidation, setForceValidation] = useState<boolean>(false);
  const setValidationDismissalDialog = useSetAtom(validationDismissalDialogAtom);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const gridApiRef = useRef<any>(null);

  const [menuState, setMenuState] = useState<{
    open: boolean;
    mouseX: number | null;
    mouseY: number | null;
    cellValue: any;
    columnField: string;
    validationError: ValidationErrorDetail | null;
  }>({
    open: false,
    mouseX: null,
    mouseY: null,
    cellValue: null,
    columnField: '',
    validationError: null,
  });

  const closeMenu = useCallback(() => {
    setMenuState((prev) => ({ ...prev, open: false }));
  }, []);

  const dispatch = useContext(MarkDispatchContext);
  const { validationSummaryByExtractionId } = useContext(MarkStateContext);

  // Clear validation state when switching extraction batches to prevent stale data
  useEffect(() => {
    dispatch({ type: 'CLEAR_VALIDATION_SUMMARY' });
    dispatch({ type: 'SET_VISIBLE_EXTRACTIONS', payload: [] });
  }, [extractionBatchId]);

  // Get transformed data for the extraction batch
  const {
    data,
    isLoading: isTransformationDataLoading,
    isError,
    isFetching: isTransformationDataFetching,
    refetch: refetchTransformed,
  } = useExtractionBatchTransformed({
    extractionBatchId,
  });

  // Get all extractions for the extraction batch
  const {
    data: allExtractions,
    isLoading: isLoadingExtractions,
    isFetching: isFetchingExtractions,
  } = useGetAllExtractions(extractionBatchId, dpid);

  // Determine if we have all extractions loaded
  const allExtractionsLoaded = useMemo(() => {
    return !isLoadingExtractions && !isFetchingExtractions;
  }, [isLoadingExtractions, isFetchingExtractions]);

  // Generate extraction IDs only when all extractions are loaded
  const extractionIds: string[] = useMemo(
    () => (allExtractionsLoaded ? (allExtractions || []).map((e: any) => e.id) : []),
    [allExtractionsLoaded, allExtractions]
  );

  // Invalidate validation queries on mount to ensure we have fresh transformed validation results
  useEffect(() => {
    if (allExtractionsLoaded && extractionIds.length > 0) {
      extractionIds.forEach((extractionId) => {
        queryClient.invalidateQueries({
          queryKey: ['extraction-validation', extractionId]
        });
      });
    }
  }, [allExtractionsLoaded, extractionIds.length]);

  // Run data validation and include transformed results once all extractions have loaded
  const validationQueries = useDataValidation(
    allExtractionsLoaded && extractionIds.length > 0,
    extractionIds,
    true,
    forceValidation,
    false
  );

  const validationInitialLoading = validationQueries.some((q: any) => q.isLoading);
  const validationLoading = validationQueries.some((q: any) => q.isLoading || q.isFetching);

  // validationFinished: true once all queries have resolved at least once (even if currently refetching)
  const validationFinished =
    validationQueries.length > 0 &&
    validationQueries.every((q: any) => q.isSuccess || q.isError);

  // validationFullySettled: true only when no fetching is happening at all (used for summary dispatch)
  const validationFullySettled = validationFinished && !validationLoading;

  // Map validation errors by document ID
  const errorsByDocumentId = useMemo<ErrorsByDocumentId>(() => {
    if (!allExtractionsLoaded || !(validationSummaryByExtractionId || []).length) {
      return {};
    }

    const extractionIdToDocumentId = new Map<string, string>(
      (allExtractions || []).map((e: any) => [String(e.id), String(getIdFromUrl(e.document))])
    );

    return (validationSummaryByExtractionId || []).reduce(
      (acc: ErrorsByDocumentId, summary: any) => {
        const documentId = extractionIdToDocumentId.get(String(summary.extractionId));
        if (!documentId) {
          return acc;
        }

        const errorDetails = (summary?.validationErrorDetails || [])
          .filter(Boolean)
          .map((err: any) => ({
            ...err,
            extractionId: String(summary.extractionId),
          })) as ValidationErrorDetail[];
        if (errorDetails.length) {
          if (!acc[documentId]) acc[documentId] = [];
          acc[documentId].push(...errorDetails);
        }
        return acc;
      },
      {} as ErrorsByDocumentId
    );
  }, [allExtractionsLoaded, allExtractions, validationSummaryByExtractionId]);

  // Calculate the total number of active (non-dismissed) errors
  const totalErrorsTransformedCount = useMemo(() => {
    return Object.values(errorsByDocumentId || {}).reduce(
      (sum: number, arr: ValidationErrorDetail[]) => {
        const filtered = (arr || []).filter(
          (err) => err.dataset === 'transformed' && !err.dismissalReason
        );
        return sum + filtered.length;
      },
      0
    );
  }, [errorsByDocumentId]);

  // Update the validation summary when validation settles (not during background refetches)
  useEffect(() => {
    if (!validationFullySettled) return;

    const summary = buildValidationSummaryByExtractionId(extractionIds, validationQueries);
    const prevSummary = validationSummaryByExtractionId || [];
    const summaryChanged = JSON.stringify(prevSummary) !== JSON.stringify(summary);

    if (summaryChanged) {
      dispatch({
        type: 'SET_VALIDATION_SUMMARY_BY_EXTRACTION_ID',
        payload: summary,
        datasetTypesToClear: ['transformed'],
      });
    }
  }, [
    validationFullySettled,
    extractionIds,
    validationQueries,
    validationSummaryByExtractionId,
    dispatch,
  ]);

  const handleRefreshValidationTransformed = async () => {
    await refetchTransformed();
    extractionIds.forEach((extractionId) => {
      queryClient.invalidateQueries({
        queryKey: ['extraction-validation', extractionId, false, true]
      });
    });
  };

  const handleForceRevalidate = () => {
    setForceValidation(true);
    extractionIds.forEach((extractionId) => {
      queryClient.removeQueries({
        queryKey: ['extraction-validation', extractionId]
      });
    });
  };

  // Reset forceValidation after validation completes
  useEffect(() => {
    if (forceValidation && !validationLoading) {
      setForceValidation(false);
    }
  }, [forceValidation, validationLoading]);

  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent) => {
      const mouseEvent = event.event as MouseEvent;
      if (mouseEvent) {
        mouseEvent.preventDefault();
      }

      const documentId = getDocumentIdFromRow(event.data);
      const colField = event.colDef?.field ?? '';
      const cellValue = event.value;

      let validationError: ValidationErrorDetail | null = null;
      if (documentId) {
        const errorsForDoc = errorsByDocumentId?.[documentId] || [];
        validationError =
          errorsForDoc.find(
            (err) => err.field === colField && err.dataset === 'transformed'
          ) ?? null;
      }

      setMenuState({
        open: true,
        mouseX: mouseEvent?.clientX ?? null,
        mouseY: mouseEvent?.clientY ?? null,
        cellValue,
        columnField: colField,
        validationError,
      });
    },
    [errorsByDocumentId]
  );

  const handleDismissError = useCallback(() => {
    if (!menuState.validationError || !dpid) return;
    const err = menuState.validationError;
    setValidationDismissalDialog({
      isOpen: true,
      dataPoolId: dpid,
      validationId: err.id,
      extractionId: err.extractionId ?? null,
      field: err.field,
      currentDismissalReason: err.dismissalReason ?? null,
      error: err.error ?? null,
      errorDetail: err.errorDetail ?? null,
      isSingleExtractionMode: true,
    });
    closeMenu();
  }, [menuState.validationError, dpid, setValidationDismissalDialog, closeMenu]);

  const rowData: any[] = useMemo(() => {
    if (!data) return [];
    try {
      const rows = csvParse(String(data));
      return rows as unknown as any[];
    } catch (e) {
      return [];
    }
  }, [data]);

  const getDocumentIdFromRow = (row: Record<string, any> | undefined | null): string => {
    if (!row) return '';
    return String(row.DocumentID ?? '');
  };

  const columnDefs: ColDef[] = useMemo(() => {
    if (rowData.length === 0) return [];

    const allKeys = new Set<string>();

    rowData.forEach((row) => Object.keys(row || {}).forEach((k) => allKeys.add(k)));

    return Array.from(allKeys).map((key) => ({
      field: key,
      headerName: key,
      sortable: true,
      filter: true,
      resizable: true,

      cellRenderer: (params: any) => {
        const documentIdKey = getDocumentIdFromRow(params?.data);
        if (!documentIdKey) return params.value ?? '';

        const errorsForDoc = errorsByDocumentId?.[documentIdKey] || [];
        const colField: string = params?.colDef?.field ?? '';
        const fieldError = errorsForDoc.find(
          (err) => err.field === colField && err.dataset === 'transformed'
        );

        if (fieldError?.dismissalReason) {
          return (
            <div style={{ position: 'relative', height: '100%' }}>
              {params.value ?? ''}
              <span
                title={`Dismissed: ${fieldError.dismissalReason}`}
                data-cy={`ignore-error-icon-${colField}`}
                style={{
                  position: 'absolute',
                  top: '2px',
                  right: '4px',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  color: 'rgb(255, 165, 0)',
                  pointerEvents: 'none',
                }}
              >
                ⚠
              </span>
            </div>
          );
        }

        return params.value ?? '';
      },

      tooltipValueGetter: (params: any): string => {
        const documentIdKey = getDocumentIdFromRow(params?.data);
        if (!documentIdKey) return '';

        const errorsForDoc = errorsByDocumentId?.[documentIdKey] || [];
        if (errorsForDoc.length === 0) return '';

        const colField: string = params?.colDef?.field ?? '';
        const fieldErrors = errorsForDoc.filter(
          (err) => err.field === colField && err.dataset === 'transformed'
        );
        const messages = fieldErrors
          .map((err) => {
            const msg = err.errorDetail ?? err.error ?? '';
            if (!msg) return '';
            return err.dismissalReason
              ? `[Dismissed: ${err.dismissalReason}] ${msg}`
              : msg;
          })
          .filter(Boolean);
        return messages.join('; ');
      },

      cellStyle: (params: any): any => {
        const documentIdKey = getDocumentIdFromRow(params?.data);
        if (!documentIdKey) return {};

        const errorsForDoc = errorsByDocumentId?.[documentIdKey] || [];
        if (errorsForDoc.length > 0) {
          const colField: string = params?.colDef?.field ?? '';
          const fieldError = errorsForDoc.find(
            (err) => err.field === colField && err.dataset === 'transformed'
          );
          if (fieldError) {
            return getCellStyles({
              accessibility: false,
              validationStatus: 'error',
              isIgnored: !!fieldError.dismissalReason,
              isHighlighted: false,
            }) as any;
          }
        }
        return {};
      },
    }));
  }, [rowData, errorsByDocumentId]);

  // Use initial loading states for showing the progress bar (hides the grid).
  // Background refetches (isFetching while cached data exists) should NOT hide the grid.
  const isBusy =
    isTransformationDataLoading ||
    isTransformationDataFetching ||
    validationInitialLoading ||
    isLoadingExtractions ||
    isFetchingExtractions;

  // Flatten all transformed validation errors for the table view (including dismissed)
  const transformedValidationErrors: Array<ValidationErrorDetail & { extractionId: string }> =
    useMemo(() => {
      const summaries = validationSummaryByExtractionId || [];
      return summaries.flatMap((extraction: any) =>
        (extraction?.validationErrorDetails || [])
          .filter((err: ValidationErrorDetail) => err?.dataset === 'transformed')
          .map((err: ValidationErrorDetail) => ({
            extractionId: String(extraction.extractionId),
            ...err,
          }))
      );
    }, [validationSummaryByExtractionId]);

  if (isError) {
    return <Typography color="error">Failed to load transformed results.</Typography>;
  }

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Transformed Data
          </Typography>
          {validationFinished && (
            <Chip
              size="small"
              label={`${totalErrorsTransformedCount}`}
              color={totalErrorsTransformedCount > 0 ? 'error' : 'success'}
              sx={{
                fontWeight: 600,
                height: 18,
                '& .MuiChip-label': {
                  padding: '0 6px',
                  lineHeight: '18px',
                  fontSize: '0.75rem',
                },
              }}
              data-cy="transformed-data-error-count"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            data-cy="button-force-revalidate"
            disabled={!allExtractionsLoaded || isBusy}
            variant="outlined"
            size="small"
            onClick={handleForceRevalidate}
            startIcon={<RefreshIcon />}
            sx={{ fontSize: '12px', height: '28px' }}
          >
            Revalidate
          </Button>
          <RefreshButton
            cypressId="button-refresh"
            disabled={!allExtractionsLoaded || isBusy}
            isLoading={isBusy}
            onClick={handleRefreshValidationTransformed}
          />
        </Box>
      </Box>
      {isBusy ? (
        <LinearProgress />
      ) : (
        <div
          ref={gridWrapperRef}
          className="ag-theme-balham"
          style={{ width: '100%' }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{ sortable: true, filter: 'agSetColumnFilter', resizable: true }}
            enableBrowserTooltips={true}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100, 200]}
            suppressNoRowsOverlay={false}
            suppressMaintainUnsortedOrder={true}
            alwaysShowHorizontalScroll={true}
            tooltipShowDelay={600}
            onCellContextMenu={handleCellContextMenu}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
            }}
          />
        </div>
      )}
      <TransformedGridContextMenu
        open={menuState.open}
        position={{ mouseX: menuState.mouseX, mouseY: menuState.mouseY }}
        cellValue={menuState.cellValue}
        validationError={menuState.validationError}
        onClose={closeMenu}
        onDismissError={handleDismissError}
        gridApi={gridApiRef.current}
      />
      {!isBusy && validationFinished && (
        <TransformedValidationErrorsTable
          errors={transformedValidationErrors}
          totalCount={totalErrorsTransformedCount}
          dpid={dpid}
          extractionBatchId={extractionBatchId}
        />
      )}
      <ValidationDismissalDialog />
    </Box>
  );
};
export default ExtractionBatchTransformedResults;
